import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const MessManagerDashboard = ({ onNavigate, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
    }
  }, [userInfo]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/mess/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      } else {
        setError(data.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const MenuItem = ({ emoji, label, active = false, route }) => (
    <div
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setSidebarOpen(false);
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const getMetrics = () => {
    if (!dashboardData?.stats) {
      return [];
    }

    const stats = dashboardData.stats;
    const totalMeals = stats.total_meals_today || 0;
    const totalStudents = stats.registered_students || 250;
    const avgWaste = stats.avg_food_waste || 15;
    const menuItems = stats.menu_items_count || 25;
    const monthlyExpense = stats.monthly_expense || 450000;
    const monthlyBudget = stats.monthly_budget || 500000;
    const stockItems = stats.stock_items || 120;
    const lowStockItems = stats.low_stock_items || 8;

    return [
      {
        label: 'Meals Served Today',
        value: totalMeals.toString(),
        total: (totalStudents * 3).toString(),
        percentage: totalStudents > 0 ? Math.round((totalMeals / (totalStudents * 3)) * 100) : 0,
        emoji: '🍽️',
        trend: '↑8%'
      },
      {
        label: 'Active Students',
        value: totalStudents.toString(),
        total: '300',
        percentage: 83,
        emoji: '👥',
        trend: '↑5%'
      },
      {
        label: 'Monthly Expense',
        value: `₹${Number(monthlyExpense / 1000).toFixed(0)}K`,
        total: `₹${Number(monthlyBudget / 1000).toFixed(0)}K`,
        percentage: monthlyBudget > 0 ? Math.round((monthlyExpense / monthlyBudget) * 100) : 0,
        emoji: '💰',
        trend: '↓12%'
      },
      {
        label: 'Food Waste',
        value: `${avgWaste}%`,
        total: '100%',
        percentage: avgWaste,
        emoji: '♻️',
        trend: '↓3%'
      },
      {
        label: 'Menu Items',
        value: menuItems.toString(),
        total: '50',
        percentage: 50,
        emoji: '📋',
        trend: '→'
      },
      {
        label: 'Stock Status',
        value: stockItems.toString(),
        total: '150',
        percentage: 80,
        emoji: '📦',
        trend: '↑7%'
      },
      {
        label: 'Low Stock Items',
        value: lowStockItems.toString(),
        total: stockItems.toString(),
        percentage: stockItems > 0 ? Math.round((lowStockItems / stockItems) * 100) : 0,
        emoji: '⚠️',
        trend: '↓5%'
      },
      {
        label: 'Student Satisfaction',
        value: '4.2/5',
        total: '5',
        percentage: 84,
        emoji: '⭐',
        trend: '↑10%'
      }
    ];
  };


  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#ffffff',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '6px solid #f3f3f3',
          borderTop: '6px solid #52ab98',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{
          color: '#52ab98',
          fontSize: '18px',
          fontWeight: '600'
        }}>Loading Dashboard...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '3rem',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#2b6777', marginBottom: '1rem' }}>Error Loading Dashboard</h2>
          <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
          <button
            onClick={fetchDashboardData}
            style={{
              backgroundColor: '#52ab98',
              color: 'white',
              border: 'none',
              padding: '12px 32px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const metrics = getMetrics();
  const orders = dashboardData?.orders || [];
  const activities = dashboardData?.activities || [];

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f4f7fa 0%, #ffffff 100%);
          overflow: hidden;
        }

        /* Sidebar Styles */
        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #2b6777 0%, #1e4d5a 100%);
          color: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          transform: translateX(-100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 32px 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .sidebar-top {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 700;
          color: white;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 4px;
          color: white;
        }

        .user-role-sidebar {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }

        .sidebar-nav {
          flex: 1;
          padding: 24px 16px;
          overflow-y: auto;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          margin-bottom: 8px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .menu-item:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateX(4px);
        }

        .menu-item-active {
          background: rgba(82, 171, 152, 0.3);
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.2);
        }

        .menu-item-active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 4px;
          background: #52ab98;
        }

        .menu-icon {
          font-size: 20px;
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .menu-label {
          font-size: 15px;
          font-weight: 500;
          flex: 1;
        }

        .menu-glow {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(82, 171, 152, 0.4) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.3s;
          pointer-events: none;
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .logout-section {
          padding: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .logout-btn {
          width: 100%;
          padding: 14px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .logout-btn:hover {
          background: rgba(255, 77, 77, 0.2);
          border-color: rgba(255, 77, 77, 0.3);
        }

        /* Overlay */
        .overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s;
          z-index: 999;
        }

        .overlay.show {
          opacity: 1;
          visibility: visible;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          width: 100%;
          overflow-y: auto;
        }

        .top-header {
          background: white;
          padding: 20px 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          background: linear-gradient(135deg, #2b6777 0%, #1e4d5a 100%);
          border: none;
          color: white;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          box-shadow: 0 4px 12px rgba(43, 103, 119, 0.3);
        }

        .menu-toggle:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(43, 103, 119, 0.4);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
        }

        .header-greeting {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .header-name {
          font-size: 20px;
          font-weight: 700;
          color: #2b6777;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .notification-btn {
          position: relative;
          background: #f8f9fa;
          border: none;
          color: #2b6777;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .notification-btn:hover {
          background: #e9ecef;
        }

        .notification-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #ff4d4d;
          color: white;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: 700;
          color: white;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        /* Notifications Panel */
        .notifications-panel {
          position: fixed;
          top: 0;
          right: -400px;
          width: 400px;
          height: 100vh;
          background: white;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          z-index: 1001;
          transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
        }

        .notifications-panel.open {
          right: 0;
        }

        .notifications-header {
          padding: 24px;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .notifications-title {
          font-size: 20px;
          font-weight: 700;
          color: #2b6777;
        }

        .close-notification-btn {
          background: #f8f9fa;
          border: none;
          color: #666;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-notification-btn:hover {
          background: #e9ecef;
        }

        .notifications-list {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
        }

        .notification-item {
          display: flex;
          gap: 16px;
          padding: 16px;
          margin-bottom: 12px;
          background: #f8f9fa;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .notification-item:hover {
          background: #e9ecef;
        }

        .notification-item.unread {
          background: #e7f5f2;
          border-left: 4px solid #52ab98;
        }

        .notification-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }

        .notification-content {
          flex: 1;
        }

        .notification-header-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .notification-title {
          font-weight: 600;
          color: #2b6777;
          font-size: 14px;
        }

        .notification-unread-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #52ab98;
        }

        .notification-message {
          font-size: 13px;
          color: #666;
          margin-bottom: 4px;
        }

        .notification-time {
          font-size: 12px;
          color: #999;
        }

        /* Content Area */
        .content-wrapper {
          padding: 32px;
        }

        .content-area {
          max-width: 1400px;
          margin: 0 auto;
        }

        .hero-section {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          padding: 48px;
          border-radius: 24px;
          margin-bottom: 32px;
          box-shadow: 0 12px 32px rgba(43, 103, 119, 0.3);
        }

        .hero-content {
          color: white;
        }

        .hero-title {
          font-size: 32px;
          font-weight: 800;
          margin-bottom: 12px;
        }

        .hero-subtitle {
          font-size: 18px;
          opacity: 0.9;
        }

        /* Metrics Grid */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .metric-card {
          background: white;
          padding: 24px;
          border-radius: 20px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
          transition: all 0.3s;
        }

        .metric-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }

        .metric-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .metric-label-section {
          flex: 1;
        }

        .metric-label {
          font-size: 14px;
          color: #666;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .metric-trend {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .metric-icon {
          font-size: 32px;
          opacity: 0.9;
        }

        .metric-value {
          font-size: 32px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 12px;
        }

        .metric-progress {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #52ab98 0%, #3d8b7a 100%);
          border-radius: 4px;
          transition: width 0.5s ease;
        }

        .progress-text {
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          min-width: 40px;
          text-align: right;
        }

        /* Chart Section */
        .chart-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .chart-card {
          background: white;
          padding: 24px;
          border-radius: 20px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .chart-title {
          font-size: 18px;
          font-weight: 700;
          color: #2b6777;
        }

        .chart-badge {
          background: #e7f5f2;
          color: #52ab98;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
        }

        .chart-container {
          padding: 20px 0;
        }

        .chart-svg {
          width: 100%;
          height: auto;
        }

        .orders-list,
        .hostels-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .order-item,
        .hostel-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          margin-bottom: 12px;
          background: #f8f9fa;
          border-radius: 12px;
          transition: all 0.2s;
        }

        .order-item:hover,
        .hostel-item:hover {
          background: #e9ecef;
        }

        .order-avatar,
        .hostel-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: white;
        }

        .order-info,
        .hostel-info {
          flex: 1;
        }

        .order-name,
        .hostel-name {
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .order-details,
        .hostel-stats {
          font-size: 13px;
          color: #666;
        }

        .order-count,
        .hostel-occupancy {
          font-weight: 700;
          color: #52ab98;
          font-size: 16px;
        }

        /* Bottom Section */
        .bottom-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
        }

        .activity-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .activity-item {
          display: flex;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid #e9ecef;
        }

        .activity-item:last-child {
          border-bottom: none;
        }

        .activity-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #52ab98;
          margin-top: 6px;
          flex-shrink: 0;
        }

        .activity-content {
          flex: 1;
        }

        .activity-text {
          color: #2b6777;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .activity-time {
          font-size: 12px;
          color: #999;
        }

        .quick-actions {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 16px;
        }

        .action-btn {
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          border: none;
          color: white;
          padding: 16px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(82, 171, 152, 0.4);
        }

        .action-btn span {
          font-size: 24px;
        }

        /* Responsive Design */

        @media (min-width: 1024px) {
          .sidebar {
            transform: translateX(0);
            position: relative;
          }

          .close-btn {
            display: none;
          }

          .overlay {
            display: none;
          }

          .menu-toggle {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 100%;
            max-width: 320px;
          }

          .top-header {
            padding: 16px 20px;
          }

          .content-wrapper {
            padding: 20px;
          }

          .hero-section {
            padding: 32px 24px;
          }

          .hero-title {
            font-size: 24px;
          }

          .hero-subtitle {
            font-size: 16px;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .chart-section,
          .bottom-section {
            grid-template-columns: 1fr;
          }

          .notifications-panel {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .header-greeting {
            display: none;
          }

          .metric-value {
            font-size: 24px;
          }

          .action-btn {
            font-size: 12px;
            padding: 12px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setSidebarOpen(false)} className="close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.managerName ? dashboardData.managerName.charAt(0).toUpperCase() : 
                  userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">
                  {dashboardData?.managerName || userInfo?.name || 'Mess Manager'}
                </div>
                <div className="user-role-sidebar">Mess Manager Portal</div>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <MenuItem emoji="🏠" label="Dashboard" active={true} route="hostel-mess-dashboard" />
            <MenuItem emoji="🍽️" label="Food Management" route="hostel-mess-food" />
            <MenuItem emoji="📊" label="Daily Report" route="hostel-mess-foodimage" />
            <MenuItem emoji="📦" label="Inventory Management" route="hostel-mess-inventory-manage" />
          </nav>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        <div
          className={`overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button onClick={() => setSidebarOpen(true)} className="menu-toggle">
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Welcome back,</div>
                <div className="header-name">{dashboardData?.managerName || userInfo?.name || 'Mess Manager'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {dashboardData?.managerName ? dashboardData.managerName.charAt(0).toUpperCase() : 
                  userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
              </div>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="content-area">
              <div className="hero-section">
                <div className="hero-content">
                  <h1 className="hero-title">Hello, {dashboardData?.managerName || userInfo?.name || 'Mess Manager'}! 🍽️</h1>
                  <p className="hero-subtitle">
                    Managing {dashboardData?.hostelName || 'Hostel Canteen'} - Today's operations overview
                  </p>
                </div>
              </div>

              <div className="metrics-grid">
                {metrics.map((metric, index) => (
                  <div key={index} className="metric-card">
                    <div className="metric-header">
                      <div className="metric-label-section">
                        <div className="metric-label">{metric.label}</div>
                        <div className="metric-trend">{metric.trend}</div>
                      </div>
                      <div className="metric-icon">{metric.emoji}</div>
                    </div>
                    <div className="metric-value">{metric.value}</div>
                    <div className="metric-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${metric.percentage}%` }}
                        />
                      </div>
                      <span className="progress-text">{metric.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="chart-section">
                <div className="chart-card">
                  <div className="chart-header">
                    <h3 className="chart-title">Today's Orders</h3>
                  </div>
                  <div className="orders-list">
                    {orders.length > 0 ? orders.map((order, index) => (
                      <div key={index} className="order-item">
                        <div className="order-avatar">
                          {order.meal_type?.charAt(0) || 'M'}
                        </div>
                        <div className="order-info">
                          <div className="order-name">{order.meal_type}</div>
                          <div className="order-details">{order.menu_item}</div>
                        </div>
                      </div>
                    )) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                        No orders for today yet
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bottom-section">
                <div className="chart-card">
                  <h3 className="chart-title">Recent Activity</h3>
                  <div className="activity-list">
                    {activities.length > 0 ? activities.map((activity, index) => (
                      <div key={index} className="activity-item">
                        <div className="activity-dot" />
                        <div className="activity-content">
                          <div className="activity-text">{activity.activity_text}</div>
                          <div className="activity-time">
                            {new Date(activity.activity_time).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                        No recent activities
                      </div>
                    )}
                  </div>
                </div>

                <div className="chart-card">
                  <h3 className="chart-title">Quick Actions</h3>
                  <div className="quick-actions">
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-mess-food')}
                    >
                      Update Menu
                      <span>🍽️</span>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-mess-foodimage')}
                    >
                      Daily Report
                      <span>📊</span>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-mess-inventory-manage')}
                    >
                      Inventory Management
                      <span>📦</span>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-mess-food')}
                    >
                      Food Management
                      <span>🍴</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MessManagerDashboard;